import { createFileRoute, Link, useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { setSearchCache } from "@/lib/page-cache";
import { buildProviderSlug } from "@/lib/provider-slug";

import { useEffect, useMemo, useRef, useState } from "react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { ProviderPhoto } from "@/components/ProviderPhoto";
import {
 searchProvidersWithMeta,
 countProvidersWithFilters,
 SEARCH_COUNT_CAP,
 cacheLastSearchResults,
 parseAiQuery,
 findInsurance,
 findProvidersByIdentifier,
 rankProvider,
 parseLocationToCityState,
 specialtyToDbPattern,
 SPECIALTIES,
 CITIES,
 INSURANCES,
 type Provider,
 type SearchMeta,
} from "@/data/heallexa";
import { aiParseSearch } from "@/lib/ai-search.functions";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { MapPin, Sparkles, Loader2, SlidersHorizontal, Search as SearchIcon } from "lucide-react";
import { ProviderCard } from "@/components/search/ProviderCard";
import { useI18n } from "@/i18n/I18nProvider";
import { BackButton } from "@/components/BackButton";
import { LocationAutocomplete } from "@/components/forms/LocationAutocomplete";
import { SpecialtySelect } from "@/components/forms/SpecialtySelect";


type SearchParams = {
 specialty?: string;
 location?: string;
 insurance?: string;
 q?: string;
 page?: number;
 pageSize?: number;
 radius?: number;
};

const US_STATE_NAME_TO_CODE = new Map<string, string>([
 ["alabama","AL"],["alaska","AK"],["arizona","AZ"],["arkansas","AR"],
 ["california","CA"],["colorado","CO"],["connecticut","CT"],["delaware","DE"],
 ["florida","FL"],["georgia","GA"],["hawaii","HI"],["idaho","ID"],
 ["illinois","IL"],["indiana","IN"],["iowa","IA"],["kansas","KS"],
 ["kentucky","KY"],["louisiana","LA"],["maine","ME"],["maryland","MD"],
 ["massachusetts","MA"],["michigan","MI"],["minnesota","MN"],["mississippi","MS"],
 ["missouri","MO"],["montana","MT"],["nebraska","NE"],["nevada","NV"],
 ["new hampshire","NH"],["new jersey","NJ"],["new mexico","NM"],["new york","NY"],
 ["north carolina","NC"],["north dakota","ND"],["ohio","OH"],["oklahoma","OK"],
 ["oregon","OR"],["pennsylvania","PA"],["rhode island","RI"],["south carolina","SC"],
 ["south dakota","SD"],["tennessee","TN"],["texas","TX"],["utah","UT"],
 ["vermont","VT"],["virginia","VA"],["washington","WA"],["west virginia","WV"],
 ["wisconsin","WI"],["wyoming","WY"],["district of columbia","DC"],
]);
const US_STATE_CODES = new Set<string>(Array.from(US_STATE_NAME_TO_CODE.values()));
const SEARCH_RESULT_LIMIT = 20;

function emptyMeta(radius: number | undefined): SearchMeta {
 return {
  requestedRadiusMiles: radius && radius > 0 ? Math.min(radius, 50) : 0,
  usedRadius: false,
  centroid: null,
 };
}

function inferStateFromText(text: string | undefined): string | undefined {
 if (!text) return undefined;
 const m = text.match(/\b([A-Za-z]{2})\b\s*$/);
 if (m) {
  const code = m[1].toUpperCase();
  if (US_STATE_CODES.has(code)) return code;
 }
 const lower = ` ${text.toLowerCase()} `;
 for (const [name, code] of US_STATE_NAME_TO_CODE) {
  if (lower.includes(` ${name} `)) return code;
 }
 return undefined;
}

function extractLocationFromSearchText(text: string): string | undefined {
 const raw = text.trim().replace(/\s+/g, " ");
 const explicit = raw.match(/\b(?:in|near|around)\s+([^?]+)$/i)?.[1];
 const candidate = (explicit || "")
  .replace(/\b(?:who|that)?\s*(?:accepts?|take|takes|with|for|open|available)\b.*$/i, "")
  .replace(/[.!?]+$/g, "")
  .trim();
 if (candidate) return candidate;
 const knownCity = CITIES.find((c) => raw.toLowerCase().includes(c.split(",")[0].toLowerCase()));
 return knownCity || undefined;
}

function extractSpecialtyFromSearchText(text: string): string | undefined {
 const lower = text.toLowerCase();
 return SPECIALTIES.find((s) => {
  const label = s.toLowerCase();
  const primary = label.split("/")[0].trim();
  const singular = primary.replace(/s\b/, "");
  return lower.includes(label) || lower.includes(primary) || lower.includes(singular);
 });
}

function SearchPendingSkeleton() {
 return (
 <div className="min-h-screen bg-background pb-1">
 <Navbar />
 <header className="pt-28 pb-10 border-b border-border bg-card">
 <div className="mx-auto max-w-6xl px-6">
 <Skeleton className="h-4 w-24 mb-3" />
 <Skeleton className="h-8 w-2/3 max-w-md mb-4" />
 <Skeleton className="h-10 w-full max-w-xl" />
 <p className="mt-4 text-sm text-muted-foreground inline-flex items-center gap-2">
 <Loader2 className="h-4 w-4 animate-spin" /> Searching…
 </p>
 </div>
 </header>
 <main className="mx-auto max-w-6xl px-6 py-12 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-8">
 <aside className="hidden md:block">
 <div className="border border-border bg-card p-4 space-y-3">
 <Skeleton className="h-4 w-24" />
 <Skeleton className="h-9 w-full" />
 <Skeleton className="h-9 w-full" />
 <Skeleton className="h-9 w-full" />
 <Skeleton className="h-9 w-full" />
 </div>
 </aside>
 <div className="space-y-4">
 {Array.from({ length: 5 }).map((_, i) => (
 <div key={i} className="border border-border bg-card p-6 flex gap-6">
 <Skeleton className="h-20 w-20 shrink-0" />
 <div className="flex-1 space-y-3">
 <Skeleton className="h-5 w-2/3" />
 <Skeleton className="h-4 w-1/2" />
 <Skeleton className="h-4 w-full" />
 <Skeleton className="h-4 w-5/6" />
 </div>
 </div>
 ))}
 </div>
 </main>
 </div>
 );
}


// Query strings may arrive with `+` representing space (form-encoded URLs from
// external links, share cards, IndexNow rewrites). decodeURIComponent does NOT
// translate `+` → space, so we must clean text params explicitly before they
// reach the database. Without this, `location=Fort+Worth%2C+TX` becomes
// "Fort+Worth, TX" and `_city = 'Fort+Worth'` matches zero providers.
function cleanQueryText(v: unknown): string | undefined {
 if (typeof v !== "string") return undefined;
 let s = v;
 // Some upstream callers double-encode; try one defensive decode pass.
 if (/%[0-9A-Fa-f]{2}/.test(s)) {
  try { s = decodeURIComponent(s); } catch { /* keep original */ }
 }
 s = s.replace(/\+/g, " ").replace(/\s+/g, " ").trim();
 return s || undefined;
}

export const Route = createFileRoute("/search")({
 validateSearch: (s: Record<string, unknown>): SearchParams => ({
 specialty: cleanQueryText(s.specialty),
 location: cleanQueryText(s.location),
 insurance: cleanQueryText(s.insurance),
 q: cleanQueryText(s.q),
 page: typeof s.page === "number" ? s.page : (typeof s.page === "string" ? Number(s.page) || undefined : undefined),
 pageSize: typeof s.pageSize === "number" ? s.pageSize : (typeof s.pageSize === "string" ? Number(s.pageSize) || undefined : undefined),
 radius: typeof s.radius === "number" ? s.radius : (typeof s.radius === "string" ? Number(s.radius) || undefined : undefined),
 }),
 loaderDeps: ({ search }) => ({
 specialty: search.specialty,
 location: search.location,
 insurance: search.insurance,
 q: search.q,
 radius: search.radius,
 page: search.page,
 }),
 staleTime: 60_000,
 loader: async ({ deps }) => {
  await setSearchCache();
  const page = Math.max(1, Number(deps.page) || 1);
  const offset = (page - 1) * SEARCH_RESULT_LIMIT;
  // Fetch PAGE_SIZE + 1 to detect whether a "next" page exists without
  // running an expensive COUNT(*) over the 6M-row providers table.
  const fetchLimit = SEARCH_RESULT_LIMIT + 1;

  if (deps.q) {
   const parsedLocal = parseAiQuery(deps.q);
   const explicitLocation = extractLocationFromSearchText(deps.q) || parsedLocal.location || deps.location;
   const explicitSpecialty = extractSpecialtyFromSearchText(deps.q) || parsedLocal.specialty;
   const localParsedLoc = parseLocationToCityState(explicitLocation);
   const inferredState =
    localParsedLoc.state ||
    inferStateFromText(deps.q) ||
    parseLocationToCityState(deps.location).state;

   const baseCity =
    (deps.radius && deps.location)
     ? deps.location
     : explicitLocation;
   const baseParsed = parseLocationToCityState(baseCity);
   const composedLocation = baseParsed.city
    ? (baseParsed.state
       ? `${baseParsed.city}, ${baseParsed.state}`
       : (inferredState ? `${baseParsed.city}, ${inferredState}` : baseParsed.city))
    : baseCity;

   const tokens = Array.from(
    new Set(
     (deps.q.match(/\b[A-Za-z0-9-]{4,20}\b/g) || []).filter((tok) => {
      if (/^\d{10}$/.test(tok)) return true;
      if (/^[A-Za-z]{0,4}\d{3,}[A-Za-z0-9-]*$/.test(tok)) return true;
      return false;
     }),
    ),
   ).slice(0, 3);

   const hasLocalFilters = !!(explicitSpecialty || explicitLocation || parsedLocal.insurance);
   const structuredQ = hasLocalFilters ? undefined : deps.q;

   const baseArgs = {
    q: structuredQ,
    specialty: explicitSpecialty || undefined,
    location: composedLocation,
    state: inferredState,
    insurance: parsedLocal.insurance || undefined,
    radiusMiles: deps.radius || 0,
   };

   const [primary, totalRes, ...idResults] = await Promise.all([
    searchProvidersWithMeta({
     ...baseArgs,
     limit: fetchLimit,
     offset,
    }).catch(() => ({ results: [] as Provider[], meta: emptyMeta(deps.radius) })),
    countProvidersWithFilters(baseArgs).catch(() => ({ count: 0, capped: false })),
    // Identifier lookup only on page 1 (it's a small global lookup, not paged).
    ...(page === 1
     ? tokens.map((t) => findProvidersByIdentifier(t, 8).catch(() => [] as Provider[]))
     : []),
   ]);

   const hasMore = primary.results.length > SEARCH_RESULT_LIMIT;
   const pageRows = primary.results.slice(0, SEARCH_RESULT_LIMIT);

   const seen = new Set<string>();
   const merged: Provider[] = [];
   for (const list of [...idResults, pageRows]) {
    for (const p of list) {
     if (!seen.has(p.id)) { seen.add(p.id); merged.push(p); }
    }
   }
   return {
    results: merged.slice(0, SEARCH_RESULT_LIMIT),
    page,
    hasMore,
    total: totalRes.count,
    totalCapped: totalRes.capped,
    filters: {
     specialty: explicitSpecialty || "",
     location: composedLocation || "",
     insurance: parsedLocal.insurance || "",
    },
    aiQuery: deps.q,
    ai: null,
    meta: primary.meta,
   };
  }

  const filters = {
   specialty: deps.specialty || "",
   location: deps.location || "",
   insurance: deps.insurance || "",
  };
  const depsParsed = parseLocationToCityState(deps.location);
  const baseArgs = {
   ...filters,
   state: depsParsed.state,
   radiusMiles: deps.radius || 0,
  };
  const [resultsRes, countRes] = await Promise.all([
   searchProvidersWithMeta({ ...baseArgs, limit: fetchLimit, offset })
    .catch((e) => {
     console.error("search loader: searchProvidersWithMeta failed", {
      ...filters, radius: deps.radius || 0, page,
      error: e instanceof Error ? { message: e.message, stack: e.stack } : e,
     });
     return { results: [] as Provider[], meta: emptyMeta(deps.radius) };
    }),
   countProvidersWithFilters(baseArgs).catch(() => ({ count: 0, capped: false })),
  ]);
  const { results, meta } = resultsRes;

  let hasMore = results.length > SEARCH_RESULT_LIMIT;
  let finalResults = results.slice(0, SEARCH_RESULT_LIMIT);
  let finalMeta = meta;
  let total = countRes.count;
  let totalCapped = countRes.capped;

  // Fallback: only on page 1, if exact city/state returned nothing, retry
  // with a looser city-only query so users never land on a blank slate.
  if (page === 1 && finalResults.length === 0 && (depsParsed.city || deps.location)) {
   try {
    const looseArgs = {
     specialty: filters.specialty,
     location: depsParsed.city || deps.location,
     insurance: filters.insurance,
     radiusMiles: 0,
    };
    const [loose, looseCount] = await Promise.all([
     searchProvidersWithMeta({ ...looseArgs, limit: fetchLimit, offset: 0 }),
     countProvidersWithFilters(looseArgs).catch(() => ({ count: 0, capped: false })),
    ]);
    if (loose.results.length > 0) {
     hasMore = loose.results.length > SEARCH_RESULT_LIMIT;
     finalResults = loose.results.slice(0, SEARCH_RESULT_LIMIT);
     finalMeta = loose.meta;
     total = looseCount.count;
     totalCapped = looseCount.capped;
    }
   } catch { /* keep empty */ }
  }
  return { results: finalResults, page, hasMore, total, totalCapped, filters, aiQuery: null as string | null, ai: null, meta: finalMeta };
 },
 pendingMs: 100,
 pendingMinMs: 300,
 pendingComponent: SearchPendingSkeleton,

 head: () => ({
 meta: [
 { title: "Search Healthcare Providers, Heallexa" },
 { name: "description", content: "Search Heallexa for doctors, dentists, therapists, and clinics by specialty, location, and insurance. AI-powered ranking." },
 { property: "og:title", content: "Search Healthcare Providers, Heallexa" },
 { property: "og:description", content: "AI-powered search for doctors, dentists, and clinics by specialty, location, and insurance." },
 { property: "og:url", content: "https://heallexa.com/search" },
 { property: "og:type", content: "website" },
 { name: "robots", content: "noindex,follow" },
 ],
 links: [{ rel: "canonical", href: "https://heallexa.com/search" }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "SearchResultsPage",
 name: "Search Healthcare Providers",
 url: "https://heallexa.com/search",
 isPartOf: { "@type": "WebSite", name: "Heallexa", url: "https://heallexa.com" },
 }),
 },
 ],
 }),
 errorComponent: ({ error }) => (
 <div className="min-h-screen flex items-center justify-center p-6 text-center">
 <div>
 <p className="text-destructive mb-4">Couldn't load results: {error.message}</p>
 <Button asChild><Link to="/">Back home</Link></Button>
 </div>
 </div>
 ),
 component: SearchPage,
});

type AiResult = {
 specialty: string;
 city: string;
 insurance: string;
 keywords: string[];
 summary: string;
 intent: string;
 lang?: "en" | "es";
};

function scoreProvider(p: Provider, ai: AiResult | null): number {
 let score = rankProvider(p) * 10;
 score += (Number(p.rating) || 0);
 if (!ai) return score;
 const hay = [
 p.name, p.practice, p.specialty, p.city, p.state ?? "",
 p.about ?? "", (p.services || []).join(" "), (p.languages || []).join(" "),
 ].join(" ").toLowerCase();
 if (ai.specialty && p.specialty.toLowerCase().includes(ai.specialty.toLowerCase())) score += 50;
 if (ai.city && p.city.toLowerCase().includes(ai.city.split(",")[0].toLowerCase())) score += 40;
 if (ai.insurance) {
 const ins = INSURANCES.find(
 (i) => i.short.toLowerCase() === ai.insurance.toLowerCase() || i.name.toLowerCase() === ai.insurance.toLowerCase()
 );
 if (ins && (p.insurance || []).includes(ins.id)) score += 30;
 }
 for (const kw of ai.keywords) {
 if (kw && hay.includes(kw.toLowerCase())) score += 8;
 }
 return score;
}

function matchedKeywords(p: Provider, ai: AiResult | null): string[] {
 if (!ai) return [];
 const hay = [
 p.name, p.practice, p.specialty, p.city,
 p.about ?? "", (p.services || []).join(" "), (p.languages || []).join(" "),
 ].join(" ").toLowerCase();
 return ai.keywords.filter((k) => k && hay.includes(k.toLowerCase()));
}

function specialtyMatches(providerSpecialty: string | null | undefined, selectedSpecialty: string): boolean {
 const provider = (providerSpecialty || "").toLowerCase();
 const selected = selectedSpecialty.trim();
 if (!selected || selected === "any") return true;
 const pattern = specialtyToDbPattern(selected);
 if (pattern) {
  try {
   return new RegExp(pattern, "i").test(provider);
  } catch { /* fall through to normalized text comparison */ }
 }
 const normalizedSelected = selected.toLowerCase().replace(/s\b/, "");
 return provider.includes(selected.toLowerCase()) || provider.includes(normalizedSelected);
}






function SearchPage() {
 const params = useSearch({ from: "/search" });
 const navigate = useNavigate({ from: "/search" });
 const { results, filters, aiQuery, ai: loaderAi, meta, page: loaderPage, hasMore, total, totalCapped } = Route.useLoaderData();
 const { t } = useI18n();
 const [ai, setAi] = useState<AiResult | null>(loaderAi as AiResult | null);
 const [aiLoading, setAiLoading] = useState(false);
 const [aiError, setAiError] = useState<string | null>(null);

 // Is the route loader currently re-running (e.g. after debounced input change)?
 const isRouteLoading = useRouterState({
 select: (s) => s.matches.some((m) => m.routeId === "/search" && m.status === "pending"),
 });

 // Debounced search input that drives the `q` URL param.
 const [queryDraft, setQueryDraft] = useState<string>(params.q ?? "");
 const lastSubmittedRef = useRef<string>(params.q ?? "");
 useEffect(() => {
 setQueryDraft(params.q ?? "");
 lastSubmittedRef.current = params.q ?? "";
 }, [params.q]);
 useEffect(() => {
 const next = queryDraft.trim();
 if (next === (lastSubmittedRef.current ?? "")) return;
 const handle = setTimeout(() => {
 lastSubmittedRef.current = next;
 navigate({
 search: (prev: any) => ({ ...prev, q: next || undefined, page: undefined }),
 replace: true,
 });
 }, 400);
 return () => clearTimeout(handle);
 }, [queryDraft, navigate]);


 // Client-side refine filters — seed from URL filters so the panel reflects deep links.
 const initialInsuranceId = useMemo(() => {
  const v = (filters.insurance || "").trim().toLowerCase();
  if (!v) return "any";
  const match = INSURANCES.find(
   (i) => i.id.toLowerCase() === v || i.short.toLowerCase() === v || i.name.toLowerCase() === v,
  );
  return match?.id ?? "any";
 }, [filters.insurance]);
 const [fSpecialty, setFSpecialty] = useState<string>(filters.specialty || "any");
 const [fCity, setFCity] = useState<string>(filters.location || "any");
 const [fInsurance, setFInsurance] = useState<string>(initialInsuranceId);
 const [fMinRating, setFMinRating] = useState<string>("any");
 const [fVerified, setFVerified] = useState(false);
 const [fPremium, setFPremium] = useState(false);
 const [fAcceptingNew, setFAcceptingNew] = useState(false);
 const [fGender, setFGender] = useState<string>("any");
 const [showFilters, setShowFilters] = useState(false);

 // Keep panel in sync when URL search params change (e.g. browser back/forward,
 // or clicking a new tile on /browse without unmounting).
 useEffect(() => {
  setFSpecialty(filters.specialty || "any");
  setFCity(filters.location || "any");
  setFInsurance(initialInsuranceId);
 }, [filters.specialty, filters.location, initialInsuranceId]);


 const resetFilters = () => {
 setFSpecialty("any"); setFCity("any"); setFInsurance("any");
 setFMinRating("any"); setFVerified(false); setFPremium(false);
 setFAcceptingNew(false); setFGender("any");
 };


 // Background AI parse: results already rendered from the loader's
 // fast local-parse path. The LLM call refines filter chips and
 // ranking without blocking the initial render.
 useEffect(() => {
  setAi((loaderAi as AiResult | null) ?? null);
  setAiError(null);
  if (!aiQuery) { setAiLoading(false); return; }
  if (loaderAi) { setAiLoading(false); return; }
  let cancelled = false;
  setAiLoading(true);
  aiParseSearch({
   data: {
    query: aiQuery,
    specialties: SPECIALTIES,
    cities: CITIES,
    insurances: INSURANCES.map((i) => i.short),
   },
  })
   .then((resp) => { if (!cancelled) setAi((resp.result as AiResult | null) ?? null); })
   .catch((e) => { if (!cancelled) setAiError(e?.message ?? "AI unavailable"); })
   .finally(() => { if (!cancelled) setAiLoading(false); });
  return () => { cancelled = true; };
 }, [loaderAi, aiQuery]);

 const ranked = useMemo(() => {
 let base: Provider[];
 if (!aiQuery) base = results;
 else {
 const withScores = results.map((p: Provider) => ({ p, s: scoreProvider(p, ai) }));
 withScores.sort((a: { p: Provider; s: number }, b: { p: Provider; s: number }) => b.s - a.s);
 if (ai && (ai.specialty || ai.city || ai.insurance || ai.keywords.length)) {
 const filtered = withScores.filter(({ p }: { p: Provider; s: number }) => {
 const specOk = !ai.specialty || p.specialty.toLowerCase().includes(ai.specialty.toLowerCase());
 const aiParsed = parseLocationToCityState(ai.city);
 const cityOk = !aiParsed.city || p.city.toLowerCase().includes(aiParsed.city.toLowerCase());
 const stateOk = !aiParsed.state || (p.state || "").toUpperCase() === aiParsed.state;
 return specOk && cityOk && stateOk;
 });
 const list = filtered.length > 0 ? filtered : withScores.slice(0, 12);
 base = list.map((x: { p: Provider; s: number }) => x.p);
 } else {
 base = withScores.map((x: { p: Provider; s: number }) => x.p);
 }
 }
 return base.filter((p) => {
  if (!specialtyMatches(p.specialty, fSpecialty)) return false;
 if (fCity !== "any") {
  const parsed = parseLocationToCityState(fCity);
  if (parsed.city && !p.city.toLowerCase().includes(parsed.city.toLowerCase())) return false;
  if (parsed.state && (p.state || "").toUpperCase() !== parsed.state) return false;
 }
 if (fInsurance !== "any" && !(p.insurance || []).includes(fInsurance)) return false;
 if (fMinRating !== "any" && (Number(p.rating) || 0) < Number(fMinRating)) return false;
  if (fVerified && p.listing_status === "unclaimed") return false;
  if (fPremium && p.listing_status !== "premium") return false;
  if (fAcceptingNew) {
   const a = ((p as any).availability || "").toLowerCase();
   const hasDays = ((p as any).available_days || []).length > 0;
   const accepting = a
    ? !/(not accepting|closed to new|waitlist|full)/.test(a)
    : hasDays;
   if (!accepting) return false;
  }
  if (fGender !== "any") {
   const g = String((p as any).gender || "").toLowerCase();
   if (!g.startsWith(fGender)) return false;
  }
  return true;
  });
  }, [results, ai, aiQuery, fSpecialty, fCity, fInsurance, fMinRating, fVerified, fPremium, fAcceptingNew, fGender]);


 useEffect(() => {
 cacheLastSearchResults(ranked);
 }, [ranked]);

 return (
 <div className="min-h-screen bg-background pb-1">
 <Navbar />
 <header className="pt-28 pb-10 border-b border-border bg-card">
 <div className="mx-auto max-w-6xl px-6">
 <BackButton className="mb-3 -ml-2" />
 {params.q ? (
 <>
 <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-1.5">
 <Sparkles className="h-3.5 w-3.5 text-primary" /> AI search
 </p>
 <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">"{params.q}"</h1>
 {aiLoading && (
 <p className="mt-3 text-sm text-muted-foreground inline-flex items-center gap-2">
 <Loader2 className="h-4 w-4 animate-spin" /> Understanding your request…
 </p>
 )}
 {ai && (
 <div className="mt-3 space-y-2">
 {ai.summary && (
 <p className="text-sm text-foreground/90 max-w-3xl">{ai.summary}</p>
 )}
 <div className="flex flex-wrap gap-1.5">
 {ai.specialty && <FilterPill label={ai.specialty} />}
 {ai.city && <FilterPill label={ai.city} />}
 {ai.insurance && <FilterPill label={ai.insurance} />}
 {ai.intent && <FilterPill label={ai.intent} muted />}
 </div>
 </div>
 )}
 {aiError && (
 <p className="mt-3 text-sm text-destructive">AI: {aiError} (showing best matches by keyword)</p>
 )}
 </>
 ) : (
 <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">
 Providers
 {filters.specialty && <> · {filters.specialty}</>}
 {filters.location && <> · {filters.location}</>}
 {filters.insurance && <> · accepts {filters.insurance}</>}
 </h1>
 )}
 <div className="mt-4 relative max-w-xl">
 <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
 <Input
 type="search"
 value={queryDraft}
 onChange={(e) => setQueryDraft(e.target.value)}
 placeholder="Refine your search…"
 className="pl-9 pr-28 h-10"
 aria-label="Refine search"
 />
 {isRouteLoading && (
 <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
 <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
 </span>
 )}
 </div>
 <p className="mt-2 text-sm text-muted-foreground">
 {isRouteLoading
 ? "Searching…"
 : ranked.length === 0
 ? "No providers found"
 : (() => {
     const startIdx = (loaderPage - 1) * PAGE_SIZE + 1;
     const endIdx = (loaderPage - 1) * PAGE_SIZE + ranked.length;
     const totalLabel = total > 0
      ? `${total.toLocaleString()}${totalCapped ? "+" : ""}`
      : `${endIdx}${hasMore ? "+" : ""}`;
     return `Showing ${startIdx}–${endIdx} of ${totalLabel} results`;
    })()}
 </p>
 {meta && meta.usedRadius && (
  <p className="mt-1 text-xs text-primary inline-flex items-center gap-1.5">
   <MapPin className="h-3 w-3" />
   Within {meta.requestedRadiusMiles} mi of {meta.city}{meta.state ? `, ${meta.state}` : ""} — including nearby cities
  </p>
 )}
 {meta && !meta.usedRadius && meta.requestedRadiusMiles > 0 && (
  <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">
   Couldn't pinpoint that city on a map — showing exact-city results only.
  </p>
 )}

 <DirectProviderLookup />

 </div>
 </header>

 <main className="mx-auto max-w-6xl px-6 py-12 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-8">
 <aside className="md:sticky md:top-20 md:self-start">
 <div className="md:hidden mb-3">
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={() => setShowFilters((v) => !v)}
 className="w-full justify-center"
 >
 <SlidersHorizontal className="h-4 w-4 mr-1" /> {t.filters.title}
 </Button>
 </div>
 <div className={`${showFilters ? "block" : "hidden"} md:block border border-border bg-card p-4 space-y-4`}>
 <div className="flex items-center justify-between">
 <h2 className="text-sm font-semibold inline-flex items-center gap-1.5">
 <SlidersHorizontal className="h-3.5 w-3.5" /> {t.filters.title}
 </h2>
 <button type="button" onClick={resetFilters} className="text-xs text-primary hover:underline">
 {t.filters.reset}
 </button>
 </div>

 <div className="space-y-1.5">
 <Label className="text-xs">{t.filters.specialty}</Label>
 <SpecialtySelect
 value={fSpecialty === "any" ? "" : fSpecialty}
 onChange={(v) => setFSpecialty(v.trim() ? v : "any")}
 placeholder={t.filters.any}
 allowCustom={false}
 />
 </div>

 <div className="space-y-1.5">
 <Label className="text-xs">{t.filters.city}</Label>
 <LocationAutocomplete
 value={fCity === "any" ? "" : fCity}
 onChange={(value) => setFCity(value.trim() ? value : "any")}
 placeholder="Any city or ZIP"
 inputClassName="h-9"
 />
 </div>

 <RadiusFilter />


 <div className="space-y-1.5">
 <Label className="text-xs">{t.filters.insurance}</Label>
 <Select value={fInsurance} onValueChange={setFInsurance}>
 <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
 <SelectContent className="max-h-72">
 <SelectItem value="any">{t.filters.any}</SelectItem>
 {INSURANCES.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-1.5">
 <Label className="text-xs">{t.filters.minRating}</Label>
 <Select value={fMinRating} onValueChange={setFMinRating}>
 <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="any">{t.filters.any}</SelectItem>
 <SelectItem value="3">3.0+</SelectItem>
 <SelectItem value="4">4.0+</SelectItem>
 <SelectItem value="4.5">4.5+</SelectItem>
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-2 pt-1">
 <label className="flex items-center gap-2 text-sm cursor-pointer">
 <Checkbox checked={fVerified} onCheckedChange={(v) => setFVerified(v === true)} />
 {t.filters.verifiedOnly}
 </label>
 <label className="flex items-center gap-2 text-sm cursor-pointer">
 <Checkbox checked={fPremium} onCheckedChange={(v) => setFPremium(v === true)} />
 {t.filters.premiumOnly}
 </label>
 <label className="flex items-center gap-2 text-sm cursor-pointer">
 <Checkbox checked={fAcceptingNew} onCheckedChange={(v) => setFAcceptingNew(v === true)} />
 Accepting new patients
 </label>
 </div>

 <div className="space-y-1.5">
 <Label className="text-xs">Provider gender</Label>
 <Select value={fGender} onValueChange={setFGender}>
 <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="any">{t.filters.any}</SelectItem>
 <SelectItem value="f">Female</SelectItem>
 <SelectItem value="m">Male</SelectItem>
 </SelectContent>
 </Select>
 </div>

 </div>
 </aside>

  <div>
 {ranked.length === 0 ? (
 <div className="border border-border bg-card p-12 text-center">
 <p className="text-muted-foreground">No providers matched your search. Try broader filters or browse by specialty.</p>
 <Button asChild className="mt-6"><Link to="/browse">Browse all</Link></Button>
 </div>
 ) : (
 <ResultsList ranked={ranked} ai={ai} page={loaderPage} hasMore={hasMore} total={total} totalCapped={totalCapped} />
 )}
 </div>
 </main>

 <FooterSection />
 </div>
 );
}

const PAGE_SIZE = 20;

function buildPageWindow(current: number, totalPages: number, windowSize = 6): number[] {
 if (totalPages <= 0) return [];
 const w = Math.min(windowSize, totalPages);
 let start = Math.max(1, current - Math.floor(w / 2));
 let end = start + w - 1;
 if (end > totalPages) {
  end = totalPages;
  start = Math.max(1, end - w + 1);
 }
 const out: number[] = [];
 for (let i = start; i <= end; i++) out.push(i);
 return out;
}

function ResultsList({
 ranked,
 ai,
 page,
 hasMore,
 total,
 totalCapped,
}: {
 ranked: Provider[];
 ai: AiResult | null;
 page: number;
 hasMore: boolean;
 total: number;
 totalCapped: boolean;
}) {
 const navigate = useNavigate({ from: "/search" });
 const start = (page - 1) * PAGE_SIZE;

 const setPage = (p: number) =>
  navigate({
   search: (prev: any) => ({ ...prev, page: p === 1 ? undefined : p }),
   replace: false,
  });

 // Derive total pages from the count when available, otherwise grow with the
 // current page + hasMore signal so users can still navigate forward.
 const knownPages = total > 0 ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 0;
 const fallbackPages = page + (hasMore ? 1 : 0);
 const totalPages = Math.max(knownPages, fallbackPages, 1);
 const pageNumbers = buildPageWindow(page, totalPages, 6);
 const totalLabel = total > 0
  ? `${total.toLocaleString()}${totalCapped ? "+" : ""} result${total === 1 ? "" : "s"}`
  : `${start + ranked.length}${hasMore ? "+" : ""} results`;

 return (
  <div className="space-y-4">
   <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
    <span>
     Showing{" "}
     <span className="font-medium text-foreground">
      {start + 1}–{start + ranked.length}
     </span>{" "}
     of <span className="font-medium text-foreground">{totalLabel}</span>
    </span>
   </div>

   {ranked.map((p) => (
    <ProviderCard key={p.id} p={p} highlights={matchedKeywords(p, ai)} />
   ))}

   <nav
    aria-label="Pagination"
    className="flex flex-wrap items-center justify-center gap-1.5 pt-6"
   >
    <button
     type="button"
     onClick={() => setPage(page - 1)}
     disabled={page <= 1}
     className="text-xs px-3 py-1.5 border border-border disabled:opacity-40 hover:border-primary/40"
     aria-label="Previous page"
    >
     ← Prev
    </button>

    {pageNumbers[0] > 1 && (
     <>
      <button
       type="button"
       onClick={() => setPage(1)}
       className="text-xs px-3 py-1.5 border border-border hover:border-primary/40"
      >
       1
      </button>
      {pageNumbers[0] > 2 && (
       <span className="text-xs px-1 text-muted-foreground">…</span>
      )}
     </>
    )}

    {pageNumbers.map((n) => (
     <button
      key={n}
      type="button"
      onClick={() => setPage(n)}
      aria-current={n === page ? "page" : undefined}
      className={
       n === page
        ? "text-xs px-3 py-1.5 border border-primary bg-primary text-primary-foreground font-medium"
        : "text-xs px-3 py-1.5 border border-border hover:border-primary/40"
      }
     >
      {n}
     </button>
    ))}

    {pageNumbers[pageNumbers.length - 1] < totalPages && (
     <>
      {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
       <span className="text-xs px-1 text-muted-foreground">…</span>
      )}
      <button
       type="button"
       onClick={() => setPage(totalPages)}
       className="text-xs px-3 py-1.5 border border-border hover:border-primary/40"
      >
       {totalPages}
       {totalCapped ? "+" : ""}
      </button>
     </>
    )}

    <button
     type="button"
     onClick={() => setPage(page + 1)}
     disabled={!hasMore && page >= totalPages}
     className="text-xs px-3 py-1.5 border border-border disabled:opacity-40 hover:border-primary/40"
     aria-label="Next page"
    >
     Next →
    </button>
   </nav>
  </div>
 );
}



function RadiusFilter() {
 const search = useSearch({ from: "/search" });
 const navigate = useNavigate({ from: "/search" });
 const current = Math.min(Math.max(Number(search.radius) || 0, 0), 50);
 const [value, setValue] = useState<number>(current);
 const parsed = parseLocationToCityState(search.location);
 const hasLocation = Boolean(parsed.city);

 useEffect(() => { setValue(current); }, [current]);

 const commit = (v: number) => {
  navigate({
   search: (prev: any) => ({ ...prev, radius: v > 0 ? v : undefined, page: undefined }),
   replace: true,
  });
 };

 return (
  <div className="space-y-1.5">
   <div className="flex items-center justify-between">
    <Label className="text-xs">Search radius</Label>
    <span className="text-xs text-muted-foreground">
     {value === 0 ? "Exact city" : `${value} mi`}
    </span>
   </div>
   <Slider
    min={0}
    max={50}
    step={5}
    value={[value]}
    onValueChange={(v) => setValue(v[0] ?? 0)}
    onValueCommit={(v) => commit(v[0] ?? 0)}
    disabled={!hasLocation}
    className="py-1"
   />
   <p className="text-[10px] text-muted-foreground leading-tight">
    {!hasLocation
     ? "Pick a city to enable radius."
     : value === 0
      ? `${parsed.city}${parsed.state ? `, ${parsed.state}` : ""} only.`
      : `Within ${value} mi of ${parsed.city}${parsed.state ? `, ${parsed.state}` : ""}, sorted by nearest.`}
   </p>
  </div>
 );
}

function FilterPill({ label, muted }: { label: string; muted?: boolean }) {
 return (
 <span
 className={
 muted
 ? "text-[11px] px-2 py-0.5 border border-border text-muted-foreground"
 : "text-[11px] px-2 py-0.5 border border-primary/40 text-primary font-medium bg-primary/5"
 }
 >
 {label}
 </span>
 );
}

function DirectProviderLookup() {
 const [open, setOpen] = useState(false);
 const [value, setValue] = useState("");
 const [results, setResults] = useState<Provider[]>([]);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);

 useEffect(() => {
 if (!open) return;
 const q = value.trim();
 if (q.length < 2) { setResults([]); setError(null); return; }
 let cancelled = false;
 setLoading(true);
 const handle = setTimeout(() => {
 findProvidersByIdentifier(q, 8)
 .then((rows) => { if (!cancelled) { setResults(rows); setError(null); } })
 .catch((e) => { if (!cancelled) setError(e.message ?? "Lookup failed"); })
 .finally(() => { if (!cancelled) setLoading(false); });
 }, 300);
 return () => { cancelled = true; clearTimeout(handle); };
 }, [value, open]);

 return (
 <div className="mt-3 max-w-xl">
 {!open ? (
 <button
 type="button"
 onClick={() => setOpen(true)}
 className="text-xs text-primary hover:underline inline-flex items-center gap-1.5"
 >
 <SearchIcon className="h-3 w-3" /> Looking for a specific provider? Find by name, NPI, license # or practice
 </button>
 ) : (
 <div className="border border-border bg-card p-3">
 <div className="flex items-center justify-between mb-2">
 <Label className="text-xs uppercase tracking-wider text-muted-foreground">
 Find a specific provider
 </Label>
 <button
 type="button"
 onClick={() => { setOpen(false); setValue(""); setResults([]); }}
 className="text-[11px] text-muted-foreground hover:text-foreground"
 >
 Close
 </button>
 </div>
 <div className="relative">
 <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
 <Input
 autoFocus
 value={value}
 onChange={(e) => setValue(e.target.value)}
 placeholder="Name, practice, NPI, or license #"
 className="pl-9 h-9"
 aria-label="Find provider by name, NPI, license number or practice"
 />
 {loading && (
 <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
 )}
 </div>
 {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
 {value.trim().length >= 2 && !loading && results.length === 0 && !error && (
 <p className="mt-2 text-xs text-muted-foreground">No providers matched.</p>
 )}
 {results.length > 0 && (
 <ul className="mt-2 divide-y divide-border border border-border bg-background">
 {results.map((p) => (
 <li key={p.id}>
 <Link
 to="/providers/$id"
 params={{ id: buildProviderSlug(p) }}
 className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50"
 >
 <ProviderPhoto provider={p} className="h-9 w-9 border border-border shrink-0" />
 <div className="min-w-0 flex-1">
 <div className="text-sm font-medium text-foreground truncate">{p.name}</div>
 <div className="text-xs text-muted-foreground truncate">
 {p.practice} · {p.specialty} · {p.city}{p.state ? `, ${p.state}` : ""}
 </div>
 </div>
 {((p as any).npi || (p as any).license_number) && (
 <div className="text-[10px] font-mono text-muted-foreground text-right shrink-0">
 {(p as any).npi && <div>NPI {(p as any).npi}</div>}
 {(p as any).license_number && <div>Lic {(p as any).license_number}</div>}
 </div>
 )}
 </Link>
 </li>
 ))}
 </ul>
 )}
 </div>
 )}
 </div>
 );
}



